// Service Worker Update Handler
//
// The Workbox SW is generated with skipWaiting + clientsClaim, which means a
// found update takes control of the running page IMMEDIATELY — the old app
// then lazy-loads chunk URLs that no longer exist in the new precache and
// dies with chunk-load errors (the "everyone needs Ctrl+Shift+R" bug).
//
// Two-part fix:
//   1. Check for updates more than once per session: hourly + whenever the
//      tab becomes visible again. SPA route changes are not navigations, so
//      without this a kept-open tab/PWA install NEVER re-checks sw.js.
//   2. When the new SW takes control mid-session, reload once so the page
//      runs the bundle its precache actually contains. The guard flag keeps
//      a broken SW from causing a reload loop.

const UPDATE_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

let reloadedForNewSW = false;

function checkForUpdates() {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration?.update?.().catch((error) => {
        console.warn('Service worker update failed:', error);
      });
    }
  }).catch((error) => {
    console.warn('Failed to get service worker registrations:', error);
  });
}

/**
 * Self-heal a poisoned precache. If the SW precaches an asset while a deploy
 * is propagating, Cloudflare Pages' SPA fallback can hand it index.html with
 * a 200 — which workbox then caches AS the JS chunk. The app is then dead on
 * every load ("Failed to fetch dynamically imported module") until the chunk
 * hash changes. Vite fires `vite:preloadError` on dynamic-import failure:
 * purge all caches + unregister the SW + reload once (session-guarded).
 */
function healPoisonedCache(): void {
  const last = Number(sessionStorage.getItem('sw_heal_attempted') || 0);
  if (Date.now() - last < 60_000) return; // no tight reload loops, but retry after 60s
  sessionStorage.setItem('sw_heal_attempted', String(Date.now()));
  // cache:'reload' refetches also repair the browser HTTP cache — a chunk that
  // got cached as HTML under immutable headers survives SW + Cache API purges.
  const assetUrls = Array.from(document.querySelectorAll<HTMLElement>('script[src], link[rel="modulepreload"][href]'))
    .map((el) => (el as HTMLScriptElement).src || (el as HTMLLinkElement).href)
    .filter((u) => /\/(ahp|luigis|antigreed|workbox)-[^/]+$/.test(u));
  Promise.allSettled([
    caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))),
    navigator.serviceWorker?.getRegistrations?.().then((regs) => Promise.all(regs.map((r) => r.unregister()))),
    Promise.all(assetUrls.map((u) => fetch(u, { cache: 'reload' }).catch(() => undefined))),
  ]).finally(() => window.location.reload());
}

export const registerSWUpdate = () => {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault(); // we handle it — don't surface the rejection
    healPoisonedCache();
  });

  if (!('serviceWorker' in navigator)) return;

  // Reload once when a new SW takes control mid-session (skipWaiting swap).
  // Skip if there was no controller before (first install — nothing stale).
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || reloadedForNewSW) return;
    reloadedForNewSW = true;
    window.location.reload();
  });

  checkForUpdates();
  setInterval(checkForUpdates, UPDATE_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdates();
  });
};
